import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { BadgeIcon } from "@/components/BadgeIcon";

// Entrega manual de una insignia a un cliente, con su motivo (lo ve el cliente).
export function AwardBadgeDialog({ user, owned = [], onClose, onDone }: { user: any | null; owned?: string[]; onClose: () => void; onDone: () => void }) {
  const [badges, setBadges] = useState<any[] | null>(null);
  const [badgeId, setBadgeId] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    setBadgeId(""); setReason(""); setError(""); setBadges(null);
    api.get("/admin/badges").then((r) => setBadges(r.data.badges.filter((b: any) => b.active))).catch(() => setBadges([]));
  }, [user]);

  const submit = async () => {
    if (!badgeId) { setError("Elige una insignia."); return; }
    if (reason.trim().length < 3) { setError("Escribe por qué la recibe (lo verá el cliente)."); return; }
    setBusy(true);
    try {
      await api.post(`/admin/users/${user.id}/badges`, { badge_id: badgeId, reason: reason.trim() });
      toast.success(`Insignia entregada a ${user.name}`);
      onClose(); onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o: boolean) => !o && !busy && onClose()}>
      <DialogContent className="bg-obsidian-card border-slate-200 text-slate-900 sm:max-w-lg" data-testid="award-badge-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">Entregar insignia</DialogTitle>
          <DialogDescription className="text-slate-600">{user?.name}</DialogDescription>
        </DialogHeader>
        {!badges ? (
          <div className="flex justify-center py-6" role="status"><Loader2 className="w-5 h-5 animate-spin text-navy" aria-hidden="true" /><span className="sr-only">Cargando…</span></div>
        ) : badges.length === 0 ? (
          <p className="text-sm text-slate-700">No hay insignias activas. Créalas en <Link to="/admin/puntos/insignias" className="text-navy underline">Puntos › Insignias</Link>.</p>
        ) : (
          <div className="space-y-4">
            <fieldset>
              <legend className="text-sm text-slate-700 mb-2">Insignia *</legend>
              <div className="grid sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                {badges.map((b) => {
                  const has = owned.includes(b.id);
                  return (
                    <label key={b.id} className={`flex items-start gap-2 p-2.5 rounded-lg border text-sm ${has ? "opacity-60 cursor-not-allowed" : "cursor-pointer"} ${badgeId === b.id ? "border-navy bg-navy/5" : "border-slate-200 hover:bg-slate-50"}`}>
                      <input type="radio" name="badge" value={b.id} disabled={has} checked={badgeId === b.id} onChange={() => { setBadgeId(b.id); setError(""); }} className="mt-1 accent-navy" />
                      <BadgeIcon icon={b.icon} size="sm" />
                      <span><span className="font-medium">{b.name}</span>{b.points > 0 && <span className="text-xs font-semibold text-gold-ink"> · +{b.points} pts</span>}
                        {has ? <span className="block text-xs text-slate-600">Ya la tiene</span> : b.description && <span className="block text-xs text-slate-600">{b.description}</span>}</span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
            <div>
              <Label htmlFor="badge-reason" className="text-sm text-slate-700">¿Por qué la recibe? * (lo verá el cliente)</Label>
              <Textarea id="badge-reason" data-testid="badge-reason" rows={2} maxLength={300} value={reason} onChange={(e: any) => { setReason(e.target.value); setError(""); }} className="mt-1.5" placeholder="Ej. Por referir a 5 amigos en octubre" />
            </div>
            {(() => { const sel = badges.find((b) => b.id === badgeId); return sel?.points > 0 && (
              <p className="text-sm text-slate-700" data-testid="award-badge-points">Con esta insignia se suman <strong>{sel.points} puntos</strong> al saldo de {user?.name}.</p>); })()}
            {error && <p className="text-sm text-red-700" role="alert">{error}</p>}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} className="border-slate-200 text-slate-700 rounded-full">Cancelar</Button>
          <Button onClick={submit} disabled={busy || !badges?.length} data-testid="award-badge-confirm" className="gold-gradient-bg text-white font-semibold rounded-full">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" aria-label="Procesando" /> : "Entregar insignia"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
